import { Dialog, Loader, useDialog } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useAuth } from 'contexts';
import { useResponse } from 'contexts/response';
import { useUserAPI } from 'hooks/use-user-api';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthenticatedUser, getUser } from 'services';
import { UserProfile } from 'types';

export const ViewUser: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile } = useAuth();
  const { resetUserPassword } = useUserAPI();
  const [loading, setLoading] = useState<boolean>(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string>();
  const { error, setError } = useResponse();
  const {
    closeDialog: closeResetPasswordDialog,
    openDialog: openResetPasswordDialog,
    ref: resetPasswordDialogRef
  } = useDialog();

  const [user, setUser] = useState<UserProfile | null>(null);

  const handleCloseResetPasswordDialog = useCallback(async () => {
    if (!loading) {
      closeResetPasswordDialog();
      setTemporaryPassword(undefined);
      setError('');
    }
  }, [closeResetPasswordDialog, loading, setError]);

  const handlePasswordReset = useCallback(async () => {
    if (!!user) {
      setLoading(true);
      const response = await resetUserPassword(user.username);
      if (!!response) {
        setTemporaryPassword(response);
      }
      setLoading(false);
    }
  }, [resetUserPassword, user]);

  useEffect(() => {
    if (params.username) {
      getUser(params.username).then(setUser);
    } else {
      getAuthenticatedUser().then(setUser);
    }
  }, [params.username, userProfile.username]);

  return (
    <>
      {user && (
        <div className="page" id="user-details">
          {params.username ? (
            <>
              <div className="page-header">
                <h1>USER {user.username ?? 'N/A'}</h1>
                <div className="flex-grow-1 d-flex">
                  {!user.active && (
                    <div className="font-weight-bold px-3 py-2 text-white" id="inactive-badge">
                      INACTIVE
                    </div>
                  )}
                </div>
                <div className="d-flex g-3 flex-row-reverse">
                  <button
                    className="btn-primary"
                    onClick={() => navigate(`edit`, { replace: true })}
                    data-cy="user-profile-edit-button"
                  >
                    EDIT
                  </button>
                  <button
                    className="btn-outline text-uppercase"
                    onClick={openResetPasswordDialog}
                    data-cy="user-profile-reset-password-button"
                  >
                    RESET PASSWORD
                  </button>
                  <button className="btn-back" onClick={() => navigate('/settings/users', { replace: true })}>
                    <span className="material-icons">arrow_back_ios</span>
                    BACK
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-fill d-flex justify-content-end g-3">
              <button
                id="edit-user"
                className="btn-primary"
                onClick={() => navigate(`users/${user.username}/edit`, { replace: true })}
                data-cy="user-profile-edit-button"
              >
                EDIT
              </button>
            </div>
          )}
          <div className="card-primary">
            <div className="card-header px-3">
              <h4 className="text-uppercase">Profile Information</h4>
            </div>
            <div className="card-body">
              <div className="grid-auto p-2">
                <div>
                  <h6 className="text-dark font-weight-regular">USERNAME</h6>
                  <p className="text-black">{user.username}</p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">EMAIL</h6>
                  <p className="text-black">{user.email}</p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">ROLE</h6>
                  <p className="text-black">{user.role}</p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">DEFAULT LOCATION</h6>
                  <p className="text-black">
                    {user.defaultLocation?.name}&nbsp;(
                    {user.defaultLocation?.state?.slice(0, 2).toUpperCase() ?? 'N/A'})
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid-auto">
            <div className="card-primary">
              <div className="card-header px-3">
                <h4 className="text-uppercase">account(s)</h4>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column align-items-start g-2">
                  {user.customers.length > 0 ? (
                    user.customers.map((customer, index) => {
                      return (
                        <div className="tag" key={index} data-cy={`user-account-${customer.id}-tag`}>
                          <p className="text-primary">{customer.name}</p>
                        </div>
                      );
                    })
                  ) : (
                    <div className="tag">
                      <p className="text-primary text-uppercase">All</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="card-primary">
              <div className="card-header px-3">
                <h4 className="text-uppercase">location(s)</h4>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column align-items-start g-2">
                  {user.locations.length > 0 ? (
                    user.locations.map((location, index) => (
                      <div className="tag" key={index} data-cy={`user-location-${location.id}-tag`}>
                        <p className="text-primary">
                          {location.name}&nbsp;({location.state?.slice(0, 2).toUpperCase() ?? 'N/A'})
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="tag">
                      <p className="text-primary text-uppercase">All</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card-primary">
              <div className="card-header px-3">
                <h4 className="text-uppercase">Trucking Company(s)</h4>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column align-items-start g-2">
                  {user.truckingCompanies.length > 0 ? (
                    user.truckingCompanies.map((truckingCompany, index) => (
                      <div className="tag" key={index} data-cy={`user-carrier-${truckingCompany.id}-tag`}>
                        <p className="text-primary">
                          {truckingCompany.name}&nbsp;({truckingCompany.scacCode.toUpperCase()})
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="tag">
                      <p className="text-primary text-uppercase">All</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Dialog closeDialog={handleCloseResetPasswordDialog} ref={resetPasswordDialogRef}>
        {!!error && (
          <div className="d-flex flex-column g-3">
            <h3 className="text-info-dark font-weight-medium">Resetting User Password Failed</h3>
            <p className="text-info-main">{error}</p>
            <div className="grid-3 mt-3">
              <button className="btn-outline col-start-3" onClick={handleCloseResetPasswordDialog}>
                close
              </button>
            </div>
          </div>
        )}
        {!!temporaryPassword && (
          <div className="d-flex flex-column g-3">
            <h3 className="text-info-dark font-weight-medium">Reset User Password</h3>
            <p className="text-info-main">User password was reset.</p>
            <p className="text-info-main">Temporary Password: {temporaryPassword}</p>
            <div className="grid-3 mt-3">
              <button className="btn-outline col-start-3" onClick={handleCloseResetPasswordDialog}>
                close
              </button>
            </div>
          </div>
        )}
        {!error && !temporaryPassword && (
          <Loader loading={loading}>
            <div className="d-flex flex-column g-3">
              <h3 className="text-info-dark font-weight-medium">Reset User Password</h3>
              <p className="text-info-main">Reset User {user?.username}'s Password?</p>
              <div className="grid-3 mt-3">
                <button className="btn-outline col-start-2" onClick={handleCloseResetPasswordDialog}>
                  close
                </button>
                <button className="btn-primary col-start-3" onClick={handlePasswordReset}>
                  confirm
                </button>
              </div>
            </div>
          </Loader>
        )}
      </Dialog>
      <ErrorDialog title="Save User Failed" />
      <SuccessDialog title="User Saved" />
    </>
  );
};
